import { type ChartArea, type Chart, type FontSpec } from "chart.js";
import { set } from "lodash";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { drawLine } from "@features/sales-v3/utils/canvas";

import {
  REVENUE_ID,
  ROAS_COLOR,
  ROAS_ID,
  SPEND_BORDER_DASH,
  SPEND_COLOR,
  SPEND_ID,
  TOTAL_KPI_COLOR,
} from "../const";

export const Y_SCALE_TITLES_PLUGIN_ID = "yScaleTitles";

export type YScaleTitlesPluginPluginOptions = {
  titles?: Record<string, string>;
  font?: FontSpec;
};

const METRIC_ORDER = [REVENUE_ID, SPEND_ID, ROAS_ID] as const;

export const drawAlternativeMetricTitle = (
  ctx: CanvasRenderingContext2D,
  metricName = "",
  {
    x,
    font,
    tickWidth,
    chartArea: { top, bottom },
    fillStyle,
    lineDash = [],
  }: {
    font: FontSpec;
    x: number;
    tickWidth: number;
    chartArea: ChartArea;
    fillStyle: string;
    lineDash?: number[];
  },
) => {
  ctx.save();
  ctx.font = `${font.weight} ${font.size}px ${font.family}`;

  ctx.strokeStyle = fillStyle;
  ctx.lineWidth = 2;

  ctx.setLineDash(lineDash);

  drawLine(ctx, { x, y: top - 1 }, { x, y: bottom + 1 });
  drawLine(ctx, { x, y: top }, { x: x + tickWidth, y: top });
  drawLine(ctx, { x, y: bottom }, { x: x + tickWidth, y: bottom });

  const measuredText = ctx.measureText(metricName);
  const textWidth = measuredText.width;
  const textHeight =
    measuredText.actualBoundingBoxAscent +
    measuredText.actualBoundingBoxDescent;

  const XOffset = x + textHeight / 2;
  const YOffset = (top + bottom) / 2 + textWidth / 2;

  ctx.fillStyle = "white";
  ctx.fillRect(
    x - textHeight / 2,
    (top + bottom) / 2 - textWidth / 2 - 5,
    textHeight,
    textWidth + 10,
  );

  ctx.fillStyle = fillStyle;
  ctx.translate(XOffset, YOffset);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(metricName, 0, 0);

  ctx.restore();
};

export const yScaleTitlesPlugin = {
  id: Y_SCALE_TITLES_PLUGIN_ID,
  options: {
    titles: {},
    font: {},
  } as YScaleTitlesPluginPluginOptions,
  afterDraw(
    chart: Chart,
    _args: any,
    options: YScaleTitlesPluginPluginOptions,
  ) {
    const { ctx, chartArea } = chart;
    const kpiScale = chart.scales[REVENUE_ID];
    const isKpiScaleVisible = isAnyDatasetVisible(chart, REVENUE_ID);
    const roasScale = chart.scales[ROAS_ID];
    const isEfficiencyMetricVisible = isAnyDatasetVisible(chart, ROAS_ID);
    const spendScale = chart.scales[SPEND_ID];
    const isSpendScaleVisible = isAnyDatasetVisible(chart, SPEND_ID);

    const kpiMetric = options.titles?.[REVENUE_ID] ?? "";
    const spendMetric = options.titles?.[SPEND_ID] ?? "";
    const efficiencyMetric = options.titles?.[ROAS_ID] ?? "";
    const font = {
      family: "Inter",
      size: 12,
      weight: "600",
      style: "normal",
      lineHeight: 16,
    } as FontSpec;

    if (isEfficiencyMetricVisible) {
      drawAlternativeMetricTitle(ctx, efficiencyMetric, {
        font,
        x: 24,
        tickWidth: 12,
        chartArea,
        fillStyle: ROAS_COLOR,
      });
    }

    if (isKpiScaleVisible) {
      drawAlternativeMetricTitle(ctx, kpiMetric, {
        font,
        x: kpiScale.right - 10,
        tickWidth: -12,
        chartArea,
        fillStyle: TOTAL_KPI_COLOR,
      });
    }

    if (isSpendScaleVisible) {
      drawAlternativeMetricTitle(ctx, spendMetric, {
        font,
        x: spendScale.right - 2,
        tickWidth: -12,
        chartArea,
        fillStyle: SPEND_COLOR,
        lineDash: SPEND_BORDER_DASH,
      });
    }
  },
};

const isAnyDatasetVisible = (chart: Chart, yAxisID: string) => {
  const { datasets } = chart.config.data;
  const isVisible = datasets.some(
    // @ts-ignore
    (dataset) => dataset.yAxisID === yAxisID && !dataset.hidden,
  );
  if (chart.config.options?.scales?.[yAxisID]?.display !== isVisible) {
    set(chart, `config.options.scales.${yAxisID}.display`, isVisible);
    chart.update();
  }
  return isVisible;
};

type SetYAxesTitlesParams = {
  chart: ChartJSOrUndefined | null;
  naming: {
    [metric: string]: string;
  };
};

export const setYAxesTitles = ({ chart, naming }: SetYAxesTitlesParams) => {
  if (chart) {
    METRIC_ORDER.forEach((metricId) => {
      set(
        chart,
        `config.options.plugins.${Y_SCALE_TITLES_PLUGIN_ID}.titles.${metricId}`,
        naming[metricId].toUpperCase(),
      );
    });
    setYAxesTitlesPadding(chart);
    chart.update();
  }
};

// Makes the chart title padding updates
const setYAxesTitlesPadding = (chart: Chart) => {
  set(chart, `config.options.scales.${ROAS_ID}.title.display`, true);
  set(chart, `config.options.scales.${ROAS_ID}.title.padding`, 0);
  set(chart, `config.options.scales.${REVENUE_ID}.title.display`, true);
  set(chart, `config.options.scales.${REVENUE_ID}.title.padding`, 8);
  set(chart, `config.options.scales.${SPEND_ID}.title.display`, true);
  set(chart, `config.options.scales.${SPEND_ID}.title.padding`, 0);
};
